import { OvCard, OvForm, OvFormContext, OvFormInput, OvFormInputReadOnly } from '@components'
import { HELPER_TEXT, LABELS, PLACEHOLDERS, TOOLTIPS } from '@constants'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Info, CheckCircle, Search, ErrorOutline } from '@mui/icons-material'
import { ISingleCompany } from '@typings'
import { observer } from 'mobx-react-lite'
import { useContext, useState, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { OvCompanyLogoUpload } from './company-logo-upload.component'
import { useStore } from '@hooks/useStore'
import { toast } from 'react-toastify'
import { CompanyKvkSearch } from './company.kvk.search'
import { KvkPrefilledProvider, useKvkPrefilled } from '../client/client.kvk.search'

const MyCompanyFormGeneralContent = observer(() => {
  const FormInput = OvFormInput<{ company: ISingleCompany }>
  const { readOnly } = useContext(OvFormContext)
  const { company } = useStore()
  const { prefilledFields } = useKvkPrefilled()
  const {
    watch,
    getValues,
    setValue,
    trigger,
    formState: { dirtyFields, errors },
  } = useFormContext()
  const tax_payable = watch('company.tax_payable')
  const bank_iban = watch('company.bank_iban')
  const [showTaxPayableDialog, setShowTaxPayableDialog] = useState(false)
  const originalIban = useRef<string | null>(null)
  const [defaultIban, setDefaultIban] = useState<string | null>(null)

  // Helper to check if a field is prefilled
  const isFieldPrefilled = (fieldName: string) => prefilledFields.has(fieldName)

  // Store original IBAN value on mount
  useEffect(() => {
    const currentIban = getValues('company.bank_iban')
    if (currentIban && !defaultIban) {
      originalIban.current = currentIban
      setDefaultIban(currentIban)
    }
  }, [getValues, defaultIban])

  // Trigger validation on tax field when tax_payable changes
  useEffect(() => {
    trigger('company.tax')
    // Clear uses_kor if tax_payable is disabled
    if (!tax_payable) {
      setValue('company.uses_kor', false, { shouldDirty: true })
    }
  }, [tax_payable, trigger, setValue])

  // Reset IBAN validation state when IBAN changes
  useEffect(() => {
    company.resetIbanValidation()
  }, [bank_iban, company])

  const handleTaxPayableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked

    // If unchecking (changing from tax-payable to non-tax-payable), show confirmation
    if (!newValue && tax_payable) {
      event.preventDefault() // Prevent the change
      setShowTaxPayableDialog(true)
    }
    // If checking, let it happen naturally - React Hook Form will handle it
  }

  const handleConfirmTaxPayableChange = () => {
    setValue('company.tax_payable', false, { shouldDirty: true })
    setShowTaxPayableDialog(false)
  }

  const handleCancelTaxPayableChange = () => {
    setShowTaxPayableDialog(false)
  }

  const handleValidateIban = async () => {
    const iban = getValues('company.bank_iban')
    await company.validateIban(iban, setValue)
  }

  return (
    <OvForm>
      <Stack
        direction="column"
        gap={4}>
        <Stack
          direction="column"
          gap={2}>
          <Typography variant="h4">Algemeen</Typography>
          {/* KVK Search - only show when not read-only */}
          {!readOnly && <CompanyKvkSearch />}
          <OvCompanyLogoUpload />
          <FormInput
            label={LABELS.COMPANY_NAME}
            name="company.name"
            required
            rules={{ required: 'Bedrijfsnaam is verplicht' }}
            highlighted={isFieldPrefilled('company.name')}
          />
          <FormInput
            label="E-mailadres bedrijf"
            name="company.email"
            type="email"
            placeholder={PLACEHOLDERS.EMAIL}
            helperText={
              'Dit e-mailadres wordt vermeld op je facturen en offertes ' +
              'en gebruikt voor correspondentie met klanten'
            }
          />
          <FormInput
            label="Telefoonnummer bedrijf"
            name="company.phone"
            placeholder={PLACEHOLDERS.PHONE}
            helperText="Dit telefoonnummer wordt vermeld op je facturen en offertes"
          />
          <Divider sx={{ my: 1 }} />
          <Alert
            severity="info"
            icon={<Info />}
            sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>KVK-nummer aanbevolen:</strong> Voor wettelijk geldige facturen in Nederland is een KVK-nummer
              verplicht. Je kunt dit later toevoegen in de instellingen.
            </Typography>
          </Alert>
          <FormInput
            label={LABELS.COC}
            name="company.coc"
            placeholder={PLACEHOLDERS.KVK_NUMBER}
            helperText={HELPER_TEXT.KVK_NUMBER}
            highlighted={isFieldPrefilled('company.coc')}
          />
          <OvFormInput
            type="checkbox"
            name="company.tax_payable"
            label="BTW plichtig"
            onChange={handleTaxPayableChange}
          />
          <OvFormInput
            required={tax_payable}
            disabled={!tax_payable}
            name="company.tax"
            label={LABELS.TAX_NUMBER}
            placeholder={PLACEHOLDERS.BTW_ID_NL}
            helperText={HELPER_TEXT.BTW_ID_NL}
            rules={{
              required: tax_payable ? 'BTW-nummer is verplicht wanneer je BTW plichtig bent' : false,
            }}
          />
          <Box>
            <OvFormInput
              type="checkbox"
              name="company.uses_kor"
              label="KOR (Kleine Ondernemingsregeling)"
              disabled={!tax_payable}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: -1, ml: 4 }}>
              {!tax_payable
                ? 'KOR is alleen beschikbaar voor BTW-plichtige bedrijven'
                : 'De KOR is een regeling voor kleine ondernemingen met een jaaromzet onder €20.000. ' +
                  'Als je voor deze regeling kiest, hoef je geen BTW aan te geven en mag je ook geen ' +
                  'BTW in rekening brengen bij je klanten. Je kunt alleen 0% BTW tarieven gebruiken.'}
            </Typography>
          </Box>
          <Stack
            direction="column"
            gap={2}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h4">Bankgegevens</Typography>
            <FormInput
              label={'IBAN'}
              name="company.bank_iban"
              placeholder={PLACEHOLDERS.IBAN}
              error={!!company.ibanError}
              helperText={
                company.ibanError ||
                (company.ibanValid
                  ? 'IBAN is gevalideerd en bankgegevens zijn ingevuld'
                  : 'Klik op de knop om je IBAN te valideren en bankgegevens op te halen')
              }
              FormHelperTextProps={{
                sx: {
                  color: company.ibanError ? 'error.main' : company.ibanValid ? 'success.main' : 'text.secondary',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {company.ibanLoading && <CircularProgress size={20} />}
                    {!company.ibanLoading && company.ibanValid && <CheckCircle color="success" />}
                    {!company.ibanLoading && company.ibanError && <ErrorOutline color="error" />}
                    {!company.ibanLoading && !company.ibanValid && !company.ibanError && (
                      <Tooltip title="Valideer IBAN">
                        <Button
                          size="small"
                          onClick={handleValidateIban}
                          disabled={!watch('company.bank_iban') || watch('company.bank_iban').length < 15}
                          sx={{ minWidth: 'auto', px: 1 }}>
                          <Search />
                        </Button>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <FormInput
              label={'BIC'}
              name="company.bank_bic"
              placeholder="ABNANL2A"
              helperText="Automatisch ingevuld bij IBAN validatie, of handmatig invoeren"
            />
            <FormInput
              label={'Naam van bank'}
              name="company.bank_name"
              placeholder="Bijv. ABN AMRO Bank"
              helperText="Automatisch ingevuld bij IBAN validatie, of handmatig invoeren"
            />
            <FormInput
              label={'Tenaamstelling'}
              name="company.bank_account_holder_name"
              placeholder={watch('company.name') || 'Naam rekeninghouder'}
              helperText={
                'Wordt gebruikt op de QR-code als rekeninghouder. ' +
                'Als dit leeg is, wordt de bedrijfsnaam gebruikt.'
              }
            />
            <Box>
              <OvFormInput
                type="checkbox"
                name="company.qr_enabled"
                label="QR-code op facturen"
                disabled={!bank_iban || bank_iban.length < 15}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: -1, ml: 4 }}>
                {!bank_iban || bank_iban.length < 15
                  ? 'QR-code is alleen beschikbaar wanneer een geldig IBAN is ingevuld'
                  : 'Voeg een QR-code toe aan facturen zodat klanten direct kunnen betalen'}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
          </Stack>
        </Stack>
        <Stack
          direction="column"
          gap={2}>
          <Typography variant="h4">Adres</Typography>
          <FormInput
            name="company.street"
            label="Straatnaam"
            placeholder={PLACEHOLDERS.STREET}
            highlighted={isFieldPrefilled('company.street')}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormInput
              name="company.house_number"
              label="Huisnummer"
              placeholder={PLACEHOLDERS.HOUSE_NUMBER}
              sx={{ flex: 2 }}
              highlighted={isFieldPrefilled('company.house_number')}
            />
            <FormInput
              name="company.house_number_addition"
              label="Toevoeging"
              helperText="Bijv. A, bis"
              sx={{ flex: 1 }}
              highlighted={isFieldPrefilled('company.house_number_addition')}
            />
          </Box>
          <FormInput
            name="company.postal_code"
            label="Postcode"
            placeholder={PLACEHOLDERS.POSTAL_CODE}
            helperText={HELPER_TEXT.POSTAL_CODE_NL}
            highlighted={isFieldPrefilled('company.postal_code')}
          />
          <FormInput
            name="company.city"
            label="Plaats"
            highlighted={isFieldPrefilled('company.city')}
          />
          <FormInput
            name="company.country_id"
            label="Land"
            options={[{ label: 'Nederland', value: 'NL-nl' }]}
            type="select"
            labelKey="label"
            valueKey="value"
            value={'NL-nl'}
            disabled
            helperText="Momenteel alleen Nederland ondersteund"
          />
        </Stack>
      </Stack>

      <Dialog
        open={showTaxPayableDialog}
        onClose={handleCancelTaxPayableChange}>
        <DialogTitle>BTW-plichtigheid wijzigen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Let op: Als u dit uitschakelt, kunt u geen BTW meer aanrekenen op nieuwe facturen en offertes. Deze
            wijziging is alleen van toepassing op nieuwe documenten, niet op documenten die u al heeft aangemaakt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Tooltip title={TOOLTIPS.CANCEL_TAX_CHANGE}>
            <Button onClick={handleCancelTaxPayableChange}>Annuleren</Button>
          </Tooltip>
          <Tooltip title={TOOLTIPS.CONFIRM_TAX_CHANGE}>
            <Button
              onClick={handleConfirmTaxPayableChange}
              variant="contained"
              autoFocus>
              Bevestigen
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </OvForm>
  )
})

// Wrapper component that provides the KVK prefilled context
export const MyCompanyFormGeneral = () => {
  return (
    <KvkPrefilledProvider>
      <MyCompanyFormGeneralContent />
    </KvkPrefilledProvider>
  )
}
